// useDashboardModalState.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Services
import { createDashboard, updateDashboard } from 'services/dashboards/DashboardWriteService';

/**
 * useDashboardModalState
 *
 * Manages draft state, reset behavior, and submit flows for DashboardModal.
 *
 * @param {object} params - Hook parameters.
 * @param {boolean} params.isOpen - Whether the modal is open.
 * @param {function} params.onClose - Called when the modal is dismissed.
 * @param {object|null} [params.dashboard=null] - Existing dashboard when editing.
 *
 * @returns {object} Grouped modal state and handlers for DashboardModal.
 */
export default function useDashboardModalState({
    isOpen,
    onClose,
    dashboard = null,
}) {
    const { user } = useUser();
    const { activeWorkspace } = useWorkspace();
    const navigate = useNavigate();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [columnCount, setColumnCount] = useState(3);
    const [isPrivate, setIsPrivate] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const editDashboardKey = String(dashboard?.key || '').trim();
    const isEditMode = Boolean(editDashboardKey);

    useEffect(() => {
        if (!isOpen) return;

        if (isEditMode) {
            setTitle(String(dashboard?.title || '').trim() || 'Untitled Dashboard');
            setDescription(String(dashboard?.description || '').trim());
            setColumnCount(Number(dashboard?.columnCount) === 2 ? 2 : 3);
            setIsPrivate(Boolean(dashboard?.private));
            setIsSubmitting(false);
            return;
        }

        setTitle('');
        setDescription('');
        setColumnCount(3);
        setIsPrivate(false);
        setIsSubmitting(false);
    }, [dashboard?.columnCount, dashboard?.description, dashboard?.private, dashboard?.title, isEditMode, isOpen]);

    const canSubmit = useMemo(() => {
        if (isSubmitting || !title.trim()) return false;
        if (isEditMode) return Boolean(editDashboardKey);
        return Boolean(activeWorkspace?.key && user?.key);
    }, [activeWorkspace?.key, editDashboardKey, isEditMode, isSubmitting, title, user?.key]);

    /**
     * handleClose
     *
     * Closes the modal unless a submit is currently in progress.
     */
    const handleClose = useCallback(() => {
        if (isSubmitting) return;
        onClose?.();
    }, [isSubmitting, onClose]);

    /**
     * handleSubmit
     *
     * Creates a new dashboard or updates the current one based on modal mode.
     *
     * @returns {Promise<void>} Resolves when the submit flow completes.
     */
    const handleSubmit = useCallback(async () => {
        if (!canSubmit) return;

        setIsSubmitting(true);

        try {
            if (isEditMode) {
                const normalizedTitle = String(title || '').trim();
                const nextTitle = normalizedTitle || 'Untitled Dashboard';
                const nextDescription = String(description || '').trim();
                const currentTitle = String(dashboard?.title || '').trim() || 'Untitled Dashboard';
                const currentDescription = String(dashboard?.description || '').trim();
                const currentPrivate = Boolean(dashboard?.private);

                if (nextTitle !== currentTitle || nextDescription !== currentDescription || isPrivate !== currentPrivate) {
                    await updateDashboard(editDashboardKey, {
                        title: nextTitle,
                        titleLower: nextTitle.toLowerCase(),
                        description: nextDescription,
                        private: isPrivate,
                    });
                }

                handleClose();
                return;
            }

            const created = await createDashboard({
                workspaceKey: activeWorkspace.key,
                userKey: user.key,
                title,
                description,
                columnCount,
                isPrivate,
                scope: 'GLOBAL',
            });

            const dashboardKey = created?.key;
            if (dashboardKey) {
                handleClose();
                navigate(`/dashboard/${dashboardKey}`);
                setTitle('');
                setDescription('');
                setColumnCount(3);
                setIsPrivate(false);
            }
        } catch (error) {
            console.error('[DashboardModal] submit dashboard error:', error);
        } finally {
            setIsSubmitting(false);
        }
    }, [activeWorkspace?.key, canSubmit, columnCount, dashboard?.description, dashboard?.private, dashboard?.title, description, editDashboardKey, handleClose, isEditMode, isPrivate, navigate, title, user?.key]);

    return {
        modalState: {
            isEditMode,
            isSubmitting,
            canSubmit,
        },
        formState: {
            title,
            setTitle,
            description,
            setDescription,
            columnCount,
            setColumnCount,
            isPrivate,
            setIsPrivate,
        },
        handlers: {
            handleClose,
            handleSubmit,
        },
    };
}